import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Pencil, Plus, Trash2 } from "lucide-react";
import api from "../../api";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../utils/apiError";
import { Customer } from "../../types";
import PageHeader from "../../components/ui/PageHeader";
import Card from "../../components/ui/Card";
import Button, { IconButton } from "../../components/ui/Button";
import DataTable, { Column, RowActions } from "../../components/ui/DataTable";
import { SearchInput, Toolbar } from "../../components/ui/Toolbar";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import Badge from "../../components/ui/Badge";
import CustomerFormModal from "./CustomerFormModal";

export default function CustomersPage() {
    const { hasRole } = useAuth();
    const isAdmin = hasRole("SUPERADMIN");
    const toast = useToast();
    const navigate = useNavigate();

    const { data, loading, error, reload } = useApi<Customer[]>("/customers");
    const [search, setSearch] = useState("");
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<Customer | null>(null);
    const [deleting, setDeleting] = useState<Customer | null>(null);

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!data) return null;
        if (!q) return data;
        return data.filter((c) =>
            [c.name, c.legalName, c.crNo, c.trn, c.contactPerson, c.mobile1, c.mobile2, c.email, String(c.customerId)]
                .filter(Boolean)
                .some((v) => v!.toLowerCase().includes(q))
        );
    }, [data, search]);

    const handleDelete = async () => {
        if (!deleting) return;
        try {
            await api.delete(`/customers/${deleting.id}`);
            toast.success("Customer deleted");
            setDeleting(null);
            reload();
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const columns: Column<Customer>[] = [
        {
            key: "id",
            header: "ID",
            className: "w-24",
            render: (c) => <span className="font-mono text-xs text-[#a78bfa]">#{c.customerId}</span>,
        },
        {
            key: "name",
            header: "Customer",
            render: (c) => (
                <div className="min-w-0">
                    <p className="font-semibold text-white truncate">{c.name}</p>
                    <p className="text-xs text-slate-500 truncate">{c.legalName}</p>
                </div>
            ),
        },
        {
            key: "cr",
            header: "CR / TRN",
            hideOnMobile: true,
            render: (c) => (
                <div className="text-xs">
                    <p className="text-slate-300">CR {c.crNo}</p>
                    <p className="text-slate-500">{c.trnNotApplicable ? "TRN n/a" : `TRN ${c.trn ?? "—"}`}</p>
                </div>
            ),
        },
        {
            key: "contact",
            header: "Contact",
            hideOnMobile: true,
            render: (c) => (
                <div className="text-xs">
                    <p className="text-slate-300">{c.contactPerson || "—"}</p>
                    <p className="text-slate-500">{c.mobile1}</p>
                </div>
            ),
        },
        ...(isAdmin
            ? [
                  {
                      key: "seller",
                      header: "Seller",
                      hideOnMobile: true,
                      render: (c: Customer) => <span className="text-slate-400">{c.seller?.name ?? "—"}</span>,
                  },
              ]
            : []),
        {
            key: "login",
            header: "Login",
            hideOnMobile: true,
            render: (c) =>
                c.user ? (
                    <Badge tone="green">
                        <KeyRound size={11} />
                        {c.user.username}
                    </Badge>
                ) : (
                    <span className="text-slate-600 text-xs">No login</span>
                ),
        },
        ...(isAdmin
            ? [
                  {
                      key: "actions",
                      header: "",
                      className: "w-24 text-right",
                      render: (c: Customer) => (
                          <RowActions>
                              <IconButton
                                  icon={Pencil}
                                  label="Edit"
                                  onClick={() => {
                                      setEditing(c);
                                      setFormOpen(true);
                                  }}
                              />
                              <IconButton icon={Trash2} label="Delete" tone="danger" onClick={() => setDeleting(c)} />
                          </RowActions>
                      ),
                  },
              ]
            : []),
    ];

    const openCreate = () => {
        setEditing(null);
        setFormOpen(true);
    };

    return (
        <>
            <PageHeader
                title="Customers"
                subtitle={isAdmin ? "All customers across resellers." : "Customers you manage."}
                actions={
                    <Button icon={Plus} onClick={openCreate}>
                        Add customer
                    </Button>
                }
            />

            <Card padded={false}>
                <Toolbar>
                    <SearchInput value={search} onChange={setSearch} placeholder="Name, CR, TRN, mobile, ID…" />
                    {data && <span className="text-xs text-slate-500 sm:ml-auto">{rows?.length ?? 0} of {data.length}</span>}
                </Toolbar>
                <DataTable
                    columns={columns}
                    rows={rows}
                    loading={loading}
                    error={error}
                    onRetry={reload}
                    rowKey={(c) => c.id}
                    onRowClick={(c) => navigate(`/customers/${c.id}`)}
                    emptyTitle={search ? "No matching customers" : "No customers yet"}
                    emptyText={search ? "Try a different search." : "Add your first customer to start tracking their products."}
                    emptyAction={
                        !search && (
                            <Button icon={Plus} size="sm" onClick={openCreate}>
                                Add customer
                            </Button>
                        )
                    }
                />
            </Card>

            <CustomerFormModal
                open={formOpen}
                customer={editing}
                onClose={() => setFormOpen(false)}
                onSaved={(c) => {
                    setFormOpen(false);
                    if (editing) reload();
                    else navigate(`/customers/${c.id}`);
                }}
            />

            <ConfirmDialog
                open={!!deleting}
                onClose={() => setDeleting(null)}
                onConfirm={handleDelete}
                title="Delete customer?"
                message={
                    <>
                        <b className="text-white">{deleting?.name}</b> (#{deleting?.customerId}) will be removed
                        {deleting?.user ? <> and their login <b className="text-white">{deleting.user.username}</b> disabled</> : null}. This
                        can't be undone.
                    </>
                }
            />
        </>
    );
}